/**
 * Which tab reads as selected for a given route path.
 *
 * Two rules, and deliberately no third:
 *
 *   1. A path under a tab's own route selects that tab.
 *   2. Anything else keeps whichever tab was last selected. Browsing away from a
 *      tab into a non-tab route (`/tabs/all` -> `/courses`) should leave the pill
 *      where the reader started, because that is where Back will return them.
 *
 * There used to be a third rule: a path starting with `/library` selected the
 * Library tab. It was wrong in both directions.
 *
 * `/library/sermon/:slug` and `/library/search` are NOT tab routes — they are
 * top-level pages that hide the tab bar (`meta.hideMasterLayoutFooter`), so the
 * rule changed nothing visible while the reader was on them. What it did do was
 * overwrite the remembered tab: a reader who reached a sermon from a Next Steps
 * lesson in the All tab came back to the lesson with the pill stuck on Library,
 * because rule 2 then faithfully kept the value rule 3 had corrupted. That became
 * reachable when lesson banners gained INTERNAL_PAGE links to library pages.
 *
 * Rule 2 already covers the case the special rule was reaching for: arriving at a
 * sermon FROM the Library tab keeps Library selected, without naming it.
 *
 * @param {string} path - the current route path
 * @param {string[]} tabRoutes - tab root paths, in tab order
 * @param {number} lastKnownIndex - index to keep when `path` belongs to no tab
 * @returns {number} index into `tabRoutes`
 */
export function tabIndexForPath(path, tabRoutes, lastKnownIndex = 0) {
  if (typeof path !== 'string' || !Array.isArray(tabRoutes)) return lastKnownIndex;

  const index = tabRoutes.findIndex(route => path === route || path.startsWith(`${route}/`));

  return index >= 0 ? index : lastKnownIndex;
}
